import { Ref, ref } from 'vue';
import { defineStore } from 'pinia';
import { merge, isEmpty } from 'lodash-es';

import apiGw from '@/services/apiGw';
import { SseListener } from '@/utils/general';
import { CardDetailsView, CardStatus, DebitCardsURI } from '@/types/Card';
import { useUserStore } from '@/store/userStore';
import { useEventStreamStore } from '@/store/eventStreamStore';
import { useContextSwitchStore } from '@/store/contextSwitchStore';
import { createResourceLoadingMixin } from '@/store/resourceLoadingMixin';
import { createResourceUri, getUidsFromUri } from '@/utils/links';
import { AgentURI } from '@/types/agent';

export interface SearchCardsQueryOptions {
  holderUid?: string[];
  embossedLine?: string[];
  holderName?: string[];
  lastFour?: string[]; //state.lastFour.length === 4 ? [state.lastFour] : undefined,
  status?: string[]; //state.status ? [state.status] : undefined,
  ownerUid?: string[]; //[contextSwitchStore.getCurrentContext()?.uid]
}

export interface LoadCardsOptionsOptions {
  enriched?: boolean;
  count: number;
  offset: number;
  forceReload?: boolean;
}

export const useCardsStore = defineStore('cards', () => {
  const eventStreamStore = useEventStreamStore();
  const contextSwitchStore = useContextSwitchStore();
  const resourceLoadingMixin = createResourceLoadingMixin();

  // STATES
  const cards: Ref<Array<CardDetailsView>> = ref([]);
  const cardsInContext: Ref<Map<string, Set<string>>> = ref(new Map());
  const staleCardsContext: Ref<{ [key: string]: number }> = ref({}); // { <uuid>: timestamp }
  const fullyLoadedCardUids: Ref<Set<string>> = ref(new Set());

  // GETTERS
  function isLoading(resourceKey: string) {
    return resourceLoadingMixin.isLoadingResource(resourceKey);
  }
  function getCardsForContext(ctxUid: string) {
    if (!ctxUid) return [];

    return cards.value.filter((c) => cardsInContext.value.get(ctxUid)?.has(c.baseCardDetails.cardUID));
  }

  const userStore = useUserStore();
  function getOwnCardsForContext(ctxUid: string) {
    if (!ctxUid) return [];

    return cards.value
      .filter((c) => cardsInContext.value.get(ctxUid)?.has(c.baseCardDetails.cardUID))
      .filter((c) => contextSwitchStore.isPrivateContext || c.baseCardDetails.holderEnriched?.uid === userStore.uid);
  }
  function getOwnNonDeletedCardsForContext(ctxUid: string) {
    return getOwnCardsForContext(ctxUid).filter(
      (c) =>
        ![CardStatus.Deleted, CardStatus.Locked, CardStatus.Blocked, CardStatus.Retired].includes(
          c.baseCardDetails.status
        )
    );
  }

  function getNbCardsWithStatusForContext(status: CardStatus, ctxUid: string) {
    if (!ctxUid) return undefined;
    const cards = getCardsForContext(ctxUid);
    // In case we do not have any card yet, return undefined.
    if (!cards || cards.length === 0) return undefined;
    return cards.filter((c) => c.baseCardDetails.status === status).length;
  }

  function getNbPendingCardsForContext(ctxUid: string) {
    if (!ctxUid) return undefined;
    const cards = getCardsForContext(ctxUid);
    // In case we do not have any card yet, return undefined.
    if (!cards || cards.length === 0) return undefined;
    return cards.filter((c) => [CardStatus.New, CardStatus.Funded].includes(c.baseCardDetails.status)).length;
  }
  function getNbActiveCardsForContext(ctxUid: string) {
    return getNbCardsWithStatusForContext(CardStatus.Active, ctxUid);
  }
  function getNbFrozonCardsForContext(ctxUid: string) {
    return getNbCardsWithStatusForContext(CardStatus.Frozen, ctxUid);
  }

  function hasStaleCardsInCurrentContext(): boolean {
    const ctxUid = contextSwitchStore.getCurrentContext()?.uid;
    if (!ctxUid) return false;
    return !!staleCardsContext.value[ctxUid];
  }

  // ACTIONS
  function $reset() {
    cards.value = [];
    cardsInContext.value = new Map();
    resourceLoadingMixin.reset();
  }

  function appendOrReplaceCards(cs: Array<CardDetailsView>) {
    cs.forEach((c) => {
      const cardIndex = cards.value.findIndex((card) => card.baseCardDetails.cardUID === c.baseCardDetails.cardUID);
      if (cardIndex >= 0) {
        const currentCard = cards.value[cardIndex];
        cards.value[cardIndex] = merge(currentCard, c);
      } else {
        cards.value.push(c);
      }
    });
  }

  async function loadCardsForCurrentContext(forceReload?: boolean) {
    let cardsUri: string | undefined;
    if (!contextSwitchStore.currentContextUid) {
      return;
    }
    if (contextSwitchStore.isPrivateContext) {
      const userStore = useUserStore();
      cardsUri = createResourceUri(AgentURI.AgentV2, { uid: userStore.uid, resource: AgentURI.Cards });
    } else {
      const options = {
        roles: ['delegate']
      };
      cardsUri = createResourceUri(AgentURI.AgentV2, {
        uid: contextSwitchStore.currentContextUid,
        resource: AgentURI.Cards,
        queryParams: options
      });
    }
    if (cardsUri) {
      loadCards(cardsUri, forceReload);
    }
  }

  async function searchCardsInCurrentContext(queryOptions: SearchCardsQueryOptions) {
    const ctxUid = contextSwitchStore.getCurrentContext()?.uid;
    if (!ctxUid) return;

    const resp = await apiGw.get(
      createResourceUri(DebitCardsURI.BaseV2, { resource: DebitCardsURI.Search, queryParams: queryOptions })
    );
    return resp.data;
  }

  async function loadCardsPaginated(uri: string, options: LoadCardsOptionsOptions): Promise<CardDetailsView[]> {
    if (!uri || uri === '') {
      console.error(`cards uri not set ${uri}`);
    }
    if (options.forceReload) {
      resourceLoadingMixin.removeResourcePaginated(`loadCards-${uri}-`);
    }
    return resourceLoadingMixin.loadResource(
      `loadCards-${uri}-${JSON.stringify(options)}`,
      async () => {
        const uid = getUidsFromUri(uri)[0];
        const response = await apiGw.get(uri, { params: options });
        let cs: CardDetailsView[];
        if (options.enriched || uri.includes('?enriched=true')) {
          cs = response.data.cardsEnriched;
        } else {
          cs = response.data;
        }
        if (!cs || isEmpty(cs)) {
          return [];
        }
        const timeOfResponse = new Date().getTime();
        const previouslyLoadedUids = cards.value.map((c) => c.baseCardDetails.cardUID);
        /**
         * Some cards may already have been loaded as part of your "own cards" and thus are mapped to the
         * partnership context as we may now want the cards related to a card program those previously
         * loaded & mapped card should also now be mapped to the current cardProgram
         */
        const alreadyMappedCards = cs.filter((c) => previouslyLoadedUids?.includes(c.baseCardDetails.cardUID)) ?? [];
        const newCards = cs.filter((c) => !previouslyLoadedUids.includes(c.baseCardDetails.cardUID)) ?? [];

        appendOrReplaceCards(cs);

        const ctxUid = getUidsFromUri(uri)[0];
        const csInContext = cardsInContext.value.get(ctxUid) || new Set();
        alreadyMappedCards.forEach((card) => csInContext.add(card.baseCardDetails.cardUID));
        newCards.forEach((card) => csInContext.add(card.baseCardDetails.cardUID));

        cardsInContext.value.set(ctxUid, csInContext);

        /**
         * In case the cards uri is marked as stale we can remove it from the stale ones
         * but only if the response came after we marked it as stale.
         */
        const staleTime = staleCardsContext.value[uid];
        if (staleTime < timeOfResponse) {
          delete staleCardsContext.value[uid];
        }
        return cs;
      },
      options.forceReload
    );
  }

  /**
   *
   * @deprecated Use loadCardsPaginated instead
   * @param uri
   * @param forceReload
   * @returns
   */
  async function loadCards(uri: string, forceReload = false): Promise<CardDetailsView[]> {
    /**
     * This could be improved by using a similar technic than in cardProgramsStore by checking for stale cards.
     * This would avoid even more unnecessary reloads.
     */
    if (!uri || uri === '') {
      console.error(`cards uri not set ${uri}`);
      // return undefined;
    }
    if (forceReload) {
      resourceLoadingMixin.removeResource(`loadCards-${uri}`);
    }
    return resourceLoadingMixin.loadResource(
      `loadCards-${uri}`,
      async () => {
        const uid = getUidsFromUri(uri)[0];
        const response = await apiGw.get(uri);
        let cs: Array<CardDetailsView>;
        if (uri.includes('?enriched=true')) {
          cs = response.data.cardsEnriched;
        } else {
          cs = response.data;
        }
        if (!cs || isEmpty(cs)) {
          return [];
        }
        const timeOfResponse = new Date().getTime();
        const previouslyLoadedUids = cards.value.map((c) => c.baseCardDetails.cardUID);
        /**
         * Some cards may already have been loaded as part of your "own cards" and thus are mapped to the
         * partnership context as we may now want the cards related to a card program those previously
         * loaded & mapped card should also now be mapped to the current cardProgram
         */
        const alreadyMappedCards = cs.filter((c) => previouslyLoadedUids?.includes(c.baseCardDetails.cardUID)) ?? [];
        const newCards = cs.filter((c) => !previouslyLoadedUids.includes(c.baseCardDetails.cardUID)) ?? [];

        appendOrReplaceCards(cs);

        const ctxUid = getUidsFromUri(uri)[0];
        const csInContext = cardsInContext.value.get(ctxUid) || new Set();
        alreadyMappedCards.forEach((card) => csInContext.add(card.baseCardDetails.cardUID));
        newCards.forEach((card) => csInContext.add(card.baseCardDetails.cardUID));

        cardsInContext.value.set(ctxUid, csInContext);

        /**
         * In case the cards uri is marked as stale we can remove it from the stale ones
         * but only if the response came after we marked it as stale.
         */
        const staleTime = staleCardsContext.value[uid];
        if (staleTime < timeOfResponse) {
          delete staleCardsContext.value[uid];
        }
        return cs;
      },
      forceReload
    );
  }

  async function getCardDetails(cardUid: string, forceReload?: boolean): Promise<CardDetailsView | undefined> {
    const card = cards.value.filter((c) => c.baseCardDetails.cardUID === cardUid)[0];
    if (card && !forceReload) {
      if (fullyLoadedCardUids.value.has(cardUid)) {
        return card;
      }
    }
    if (forceReload) {
      resourceLoadingMixin.removeResource(`getCardDetails-${cardUid}`);
    }
    return resourceLoadingMixin.loadResource(
      `getCardDetails-${cardUid}`,
      async () => {
        const response = await apiGw.get(`/v2/debit-cards/${cardUid}`);
        const timeOfResponse = new Date().getTime();
        unstaleCardUidIfOlder(cardUid, timeOfResponse);

        const cardIndex = cards.value.findIndex((c) => c.baseCardDetails.cardUID === cardUid);
        if (forceReload) {
          cards.value[cardIndex] = response.data;
        } else {
          cards.value[cardIndex] = merge(card, response.data);
        }
        fullyLoadedCardUids.value.add(cardUid);

        return cards.value[cardIndex];
      },
      forceReload
    );
  }

  async function actionsForCard(card: CardDetailsView, forceReload = false) {
    if (forceReload) {
      resourceLoadingMixin.removeResource(`actionsForCard-${card.baseCardDetails.cardUID}`);
    }
    return resourceLoadingMixin.loadResource(
      `actionsForCard-${card.baseCardDetails.cardUID}`,
      async () => {
        const response = await apiGw.get(`/v2/debit-cards/${card.baseCardDetails.cardUID}/actions`);
        return response.data;
      },
      forceReload
    );
  }

  function updateCardStatus(cardUid: string, status: CardStatus) {
    const card = cards.value.filter((c) => c.baseCardDetails.cardUID === cardUid)[0];
    if (card) {
      card.baseCardDetails.status = status;
    }
  }

  function unstaleCardUidIfOlder(uid: string, timeOfResponse: number) {
    if (!eventStreamStore.stale_debit_cards) return;

    const staleTime = eventStreamStore.stale_debit_cards[uid]?.time;
    if (staleTime < timeOfResponse) {
      eventStreamStore.unstaleUid(uid, SseListener.DebitCard);
    }
  }

  function clearCardsForContext(ctxUid: string) {
    if (!ctxUid) return;
    cards.value = cards.value.filter((c) => !cardsInContext.value.get(ctxUid)?.has(c.baseCardDetails.cardUID));
    cardsInContext.value.delete(ctxUid);
  }

  return {
    // STATES
    isLoading,
    cards,
    cardsInContext,
    staleCardsContext,
    // GETTERS
    getCardsForContext,
    getOwnCardsForContext,
    getOwnNonDeletedCardsForContext,
    getNbActiveCardsForContext,
    getNbFrozonCardsForContext,
    getNbPendingCardsForContext,
    getNbCardsWithStatusForContext,
    hasStaleCardsInCurrentContext,
    // ACTIONS
    $reset,
    appendOrReplaceCards,
    loadCardsForCurrentContext,
    searchCardsInCurrentContext,
    loadCards,
    loadCardsPaginated,
    getCardDetails,
    actionsForCard,
    updateCardStatus,
    clearCardsForContext
  };
});
